import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import ReactMarkdown from 'react-markdown';
import {
  Search,
  Loader2,
  ExternalLink,
  Sparkles,
  FileText,
  Library,
  Bookmark,
  AlertCircle,
  Filter,
  Copy,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DOC_TYPES = [
  { id: 'all', label: 'All', icon: Library },
  { id: 'circular', label: 'Circulars', icon: FileText },
  { id: 'notification', label: 'Notifications', icon: Bookmark },
  { id: 'instruction', label: 'Instructions', icon: Filter },
  { id: 'trade_notice', label: 'Trade Notices', icon: AlertCircle },
];

const EXAMPLE_QUERIES = [
  { label: 'Input tax credit blocked credits', q: 'input tax credit blocked credits' },
  { label: 'GST refund — inverted duty structure', q: 'refund inverted duty structure' },
  { label: 'Circular No. 183/15/2022-GST', q: 'Circular No. 183/15/2022-GST' },
  { label: 'GST on directors remuneration', q: 'GST directors remuneration' },
];

// Markdown rendering components matching the rest of the app
const mdComponents = {
  h1: ({ children }) => <h1 className="text-xl font-bold text-[#0F172A] mt-3 mb-2 border-b border-slate-200 pb-1">{children}</h1>,
  h2: ({ children }) => <h2 className="text-lg font-bold text-[#1E3A8A] mt-4 mb-2">{children}</h2>,
  h3: ({ children }) => <h3 className="text-base font-semibold text-slate-800 mt-3 mb-1.5">{children}</h3>,
  p: ({ children }) => <p className="text-sm text-slate-700 leading-relaxed mb-2">{children}</p>,
  strong: ({ children }) => <strong className="font-semibold text-slate-900">{children}</strong>,
  em: ({ children }) => <em className="italic text-slate-600">{children}</em>,
  ul: ({ children }) => <ul className="list-disc pl-5 mb-2 space-y-0.5">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-5 mb-2 space-y-0.5">{children}</ol>,
  li: ({ children }) => <li className="text-sm text-slate-700 leading-relaxed">{children}</li>,
  hr: () => <hr className="my-3 border-slate-200" />,
};

// Single search-result card with on-demand summary
const ResultCard = ({ result, index }) => {
  const [summary, setSummary] = useState(null);
  const [contentStatus, setContentStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const handleSummarize = useCallback(async () => {
    if (summary || isLoading) {
      setExpanded((v) => !v);
      return;
    }
    setIsLoading(true);
    try {
      const res = await axios.post(`${API}/cbic/summarize`, {
        url: result.url,
        title: result.title,
        snippet: result.snippet,
      });
      setSummary(res.data.summary);
      setContentStatus(res.data.content_status);
      setExpanded(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate summary');
    } finally {
      setIsLoading(false);
    }
  }, [result, summary, isLoading]);

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  return (
    <Card className="border border-slate-200 hover:border-[#1E3A8A]/30 transition-all" data-testid={`cbic-result-${index}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 bg-[#1E3A8A]/10 rounded-lg flex items-center justify-center flex-shrink-0">
            <FileText className="w-4 h-4 text-[#1E3A8A]" />
          </div>
          <div className="flex-1 min-w-0">
            <a
              href={result.url}
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-[#0F172A] hover:text-[#1E3A8A] text-sm leading-snug flex items-start gap-1.5 group"
              data-testid={`cbic-result-link-${index}`}
            >
              <span className="line-clamp-2">{result.title || result.url}</span>
              <ExternalLink className="w-3.5 h-3.5 mt-0.5 text-slate-400 group-hover:text-[#1E3A8A] flex-shrink-0" />
            </a>
            <p className="text-xs text-slate-500 mt-1 break-all line-clamp-1">{result.url}</p>
            {result.snippet && (
              <p className="text-sm text-slate-600 mt-2 leading-relaxed line-clamp-3">{result.snippet}</p>
            )}

            <div className="flex items-center gap-2 mt-3">
              <Button
                size="sm"
                variant="outline"
                onClick={handleSummarize}
                disabled={isLoading}
                className={summary ? 'border-[#1E3A8A]/30 text-[#1E3A8A]' : 'border-slate-200'}
                data-testid={`cbic-summarize-btn-${index}`}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                    Summarising...
                  </>
                ) : summary ? (
                  <>
                    <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                    {expanded ? 'Hide Summary' : 'Show Summary'}
                  </>
                ) : (
                  <>
                    <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                    Generate AI Summary
                  </>
                )}
              </Button>
              <a
                href={result.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-medium text-slate-500 hover:text-[#1E3A8A] inline-flex items-center gap-1"
              >
                Open on CBIC <ExternalLink className="w-3 h-3" />
              </a>
            </div>

            {expanded && summary && (
              <div className="mt-3 bg-gradient-to-br from-[#FDFBF7] to-amber-50/40 border border-amber-100 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-[#F59E0B]" />
                    <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">AI Summary</span>
                    {contentStatus === 'metadata_only' && (
                      <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">Snippet-based</span>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleCopy(summary)}
                    className="h-7 px-2"
                    data-testid={`cbic-copy-summary-${index}`}
                  >
                    <Copy className="w-3 h-3 mr-1" />Copy
                  </Button>
                </div>
                <div className="cbic-summary-md">
                  <ReactMarkdown components={mdComponents}>{summary}</ReactMarkdown>
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default function CBICSearchTab() {
  const [query, setQuery] = useState('');
  const [docType, setDocType] = useState('all');
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);

  const handleSearch = useCallback(async () => {
    if (!query.trim() || isSearching) return;
    setIsSearching(true);
    setHasSearched(true);
    try {
      const res = await axios.post(`${API}/cbic/search`, {
        query: query.trim(),
        doc_type: docType,
      }, { timeout: 30000 });
      setResults(res.data.results || []);
      if ((res.data.results || []).length === 0) {
        toast.info('No CBIC documents found. Try a broader query.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'CBIC search failed');
      setResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [query, docType, isSearching]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Search Bar */}
      <div className="border-b border-slate-200 bg-white p-5">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#1E3A8A] to-[#2563EB] flex items-center justify-center">
              <Library className="w-3.5 h-3.5 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-800">CBIC Circular Search</h3>
              <p className="text-xs text-slate-500">Search circulars, notifications & instructions from cbic.gov.in</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder='Keyword (e.g., "input tax credit") or Circular Number (e.g., "183/15/2022-GST")'
                className="w-full pl-10 pr-4 h-11 rounded-xl border border-slate-200 focus:border-[#1E3A8A] focus:ring-2 focus:ring-[#1E3A8A]/20 outline-none text-sm text-slate-700 placeholder:text-slate-400 bg-slate-50/50"
                disabled={isSearching}
                data-testid="cbic-search-input"
              />
            </div>
            <Button
              onClick={handleSearch}
              disabled={!query.trim() || isSearching}
              className="bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white h-11 px-5"
              data-testid="cbic-search-btn"
            >
              {isSearching ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Search className="w-4 h-4 mr-1.5" />Search</>}
            </Button>
          </div>

          {/* Doc type filters */}
          <div className="flex items-center gap-1.5 mt-3 flex-wrap">
            {DOC_TYPES.map((t) => (
              <button
                key={t.id}
                onClick={() => setDocType(t.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all flex items-center gap-1.5 ${
                  docType === t.id
                    ? 'bg-[#1E3A8A] text-white shadow-sm'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid={`cbic-type-${t.id}`}
              >
                <t.icon className="w-3 h-3" />
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Results */}
      <ScrollArea className="flex-1 p-6">
        <div className="max-w-4xl mx-auto">
          {!hasSearched ? (
            <div className="text-center pt-16">
              <div className="w-16 h-16 mx-auto mb-5 bg-gradient-to-br from-[#1E3A8A] to-[#3B82F6] rounded-2xl flex items-center justify-center shadow-lg">
                <Library className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">Search CBIC Documents</h3>
              <p className="text-slate-500 mb-6 max-w-md mx-auto text-sm">
                Find circulars, notifications, instructions and trade notices issued by the Central Board of Indirect Taxes & Customs.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-xl mx-auto text-left">
                {EXAMPLE_QUERIES.map((ex, i) => (
                  <button
                    key={i}
                    onClick={() => { setQuery(ex.q); }}
                    className="p-3 rounded-xl border border-slate-200 hover:border-[#1E3A8A]/30 hover:bg-[#1E3A8A]/5 transition-all"
                    data-testid={`cbic-example-${i}`}
                  >
                    <p className="text-xs font-medium text-slate-700">{ex.label}</p>
                    <p className="text-[11px] text-slate-400 mt-0.5 truncate">{ex.q}</p>
                  </button>
                ))}
              </div>
            </div>
          ) : isSearching ? (
            <div className="text-center pt-20">
              <Loader2 className="w-10 h-10 text-[#1E3A8A] animate-spin mx-auto mb-4" />
              <p className="text-sm text-slate-600">Searching CBIC repositories...</p>
            </div>
          ) : results.length === 0 ? (
            <div className="text-center pt-20">
              <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="font-medium text-slate-700 mb-1">No results found</p>
              <p className="text-sm text-slate-500">Try a different keyword or broaden your search.</p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  {results.length} result{results.length !== 1 ? 's' : ''} on cbic.gov.in
                </p>
              </div>
              {results.map((r, i) => (
                <ResultCard key={`${r.url}-${i}`} result={r} index={i} />
              ))}
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
